<template>
  <ListViewBanner>
    Revisions
  </ListViewBanner>

  <AppContainer>
    <AppTable>
      <AppTableHeader>
        <th>Revision Number</th>
        <th>Date</th>
        <th>User</th>
        <th><span class="sr-only">Actions</span></th>
      </AppTableHeader>

      <AppTableBody
        :loading="loading"
        :no-results="!revisions.length"
        :colspan="4"
      >
        <template v-if="revisions.length">
          <tr
            v-for="revision in revisions"
            :key="revision.id"
            class="overflow-visible"
          >
            <td>{{ revision.revisionNumber }}</td>

            <td>
              {{ dateFormatter.formatShortDate(revision.createdAt) }}
            </td>
            <td>
              {{ revision.createdByUser ? `${revision.createdByUser.firstName} ${revision.createdByUser.lastName}` : '-' }}
            </td>

            <td>
              <AppButtonGroup>
                <RouterLink
                  :to="{ name: 'app_revision_show', params: {revision: revision.id} }"
                  class="btn btn-outline-gray text-gray-500"
                  target="_blank"
                >
                  View
                </RouterLink>

                <AppButton
                  class="btn-outline-gray"
                  @click="revisionToRevert = revision"
                >
                  Revert
                </AppButton>
              </AppButtonGroup>
            </td>
          </tr>
        </template>
      </AppTableBody>
    </AppTable>

    <AppPagination
      v-model:itemsPerPage="paginationData.itemsPerPage"
      v-model:currentPage="paginationData.page"
      :items-per-page="paginationData?.itemsPerPage"
      :current-page="paginationData?.page"
      :total-items="paginationData?.total"
      @page-change="searchParams.page = $event"
      @items-per-page-change="searchParams.itemsPerPage = $event"
    />

    <template v-if="revisionToRevert">
      <ConfirmationModal
        :title="`Are you sure that you want to revert to revision #${revisionToRevert.revisionNumber}`"
        :processing="isReverting"
        @confirm="revertToRevision"
        @close="revisionToRevert = null"
      >
        <p>This will replace the current draft plan with the selected revision. If this plan has already been published, the published plan will not be affected.</p>
        <p>All changes are versioned, so you can always undo this change.</p>
      </ConfirmationModal>
    </template>
  </AppContainer>
</template>

<script setup>
import { HttpStatusCode } from 'axios';
import { ref, watchEffect } from 'vue';
import { useRoute } from 'vue-router';

import { useDateFormatter, useHttp, useToasts } from '@/composables';

import { AppContainer, AppPagination } from '@/components/app';
import { ListViewBanner } from '@/components/banners';
import { AppButton } from '@/components/buttons';
import { ConfirmationModal } from '@/components/modals';
import { AppTable, AppTableBody, AppTableHeader } from '@/components/tables';
import { AppButtonGroup } from '@/components/app/index.js';

const dateFormatter = useDateFormatter();
const route = useRoute();
const toasts = useToasts();

const loading = ref();
const revisions = ref([]);
const paginationData = ref({
  page: 1,
  itemsPerPage: 10,
});
const searchParams = ref({
  page: 1,
});
const revisionToRevert = ref();
const isReverting = ref();

const loadPlan = async () => {
  const response = await useHttp().get(`/districts/${route.params.district}/plans/${route.params.plan}`);

  if (response.statusCode === HttpStatusCode.Ok) {
    const plan = response.data;
  }
};

const loadRevisions = async () => {
  loading.value = true;
  revisions.value = [];

  const response = await useHttp().get(`/plans/${route.params.plan}/revisions`, searchParams.value);

  if (response.statusCode === HttpStatusCode.Ok) {
    revisions.value = response.data.items;
    paginationData.value = response.data.pagination;
  }

  loading.value = false;
};

const revertToRevision = async () => {
  isReverting.value = true;

  const response = await useHttp().post(`/plans/${route.params.plan}/revisions/${revisionToRevert.value.id}/revert`);

  if (response.statusCode === HttpStatusCode.Ok) {
    toasts.success(`Successfully reverted to revision #${revisionToRevert.value.revisionNumber}`);

    await loadRevisions();
  }

  isReverting.value = false;
  revisionToRevert.value = null;
};

watchEffect(loadRevisions);
loadPlan();
</script>
